import { useContext } from 'react';

import ReactHtmlParser from 'react-html-parser';
import { useNavigate, useParams } from 'react-router';

import { Box, Button, Card, Checkbox, FormControlLabel, Grid, Stack, styled, Typography } from '@mui/material';

import { ConfirmDialog } from '../../../components/dialogs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners/Spinner';
import { useGetPostById, useRecommendPost, useUnRecommendPost, useUpdatePost } from '../../../lib/post/hooks';
import { extractImagesFromPostContent, getAuthorName } from '../../../lib/util/helpers';
import { Post } from '../../../types/Post';

import PostSidebar from './PostSidebar';

function PostEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const postId = id ?? '';
  const getPost = useGetPostById(postId);
  const updatePost = useUpdatePost();

  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const { mutateAsync: recommendPost } = useRecommendPost();
  const { mutateAsync: unRecommendPost } = useUnRecommendPost();

  const postContent = (data: any) => {
    const { images, cleanedContent } = extractImagesFromPostContent(data);

    return (
      <>
        <Box sx={{ display: 'flex', gap: '24px' }}>
          {images.map((content, index) => (
            <Box
              key={index}
              component="img"
              sx={{
                width: '250px',
                height: '250px',
                objectFit: 'cover',
              }}
              alt="Comment Image"
              src={content.src}
            />
          ))}
        </Box>
        {ReactHtmlParser(cleanedContent)}
      </>
    );
  };

  const handlePostEnableOverride = async () => {
    await updatePost.mutate({ postId: postId, params: { editAllowed: true } });
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  };

  const handleRecommendPost = async (post: Post) => {
    try {
      if (post.recommended) {
        await unRecommendPost({ postId: post.id });
      } else {
        await recommendPost({ postId: post.id });
      }
    } catch (error) {
      setAlertSnackbar({
        message: (error as any).message,
        severity: 'error',
        vertical: 'top',
      });
    }
  };

  return (
    <Container>
      {getPost.isLoading && <Spinner />}
      {!getPost.isLoading && getPost.data && (
        <>
          <TopBar>
            <Typography variant="h5">Edit Article </Typography>
          </TopBar>

          <Stack flexDirection="row" gap={2}>
            <StyledCard sx={{ flex: 2 }}>
              <StyledGrid item xs={12}>
                <Typography>Post Title: {getPost.data.title}</Typography>
              </StyledGrid>
              <StyledGrid item xs={12}>
                <Typography>Post Author: {getAuthorName(getPost.data.author)}</Typography>
              </StyledGrid>
              <StyledGrid item xs={12}>
                <StyledTypography>Content: </StyledTypography>
                {postContent(getPost.data.content)}
              </StyledGrid>
              <StyledGrid item xs={12}>
                {getPost.data.description && <Typography>Description: {getPost.data.description}</Typography>}
              </StyledGrid>

              <StyledGrid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={Boolean(getPost.data.recommended)} onChange={() => handleRecommendPost(getPost.data)} />}
                  label="Recommended"
                />
              </StyledGrid>

              <ButtonContainer>
                <Buttons>
                  <Button onClick={() => navigate(-1)} type="button">
                    Cancel
                  </Button>
                  <ConfirmDialog
                    trigger={(open) => (
                      <Button onClick={open} type="button" variant="contained">
                        Enable Override
                      </Button>
                    )}
                    title="Post Enable Override"
                    description="Enabling this feature will allow the post to be edited for the next 24 hours. Do you want to proceed?"
                    cancelBtnText={'Cancel'}
                    submitBtnText={'Submit'}
                    onConfirm={async (close) => {
                      handlePostEnableOverride();
                      close();
                    }}
                  />
                </Buttons>
              </ButtonContainer>
            </StyledCard>

            <StyledCard sx={{ flex: 1 }}>
              <PostSidebar post={getPost.data} />
            </StyledCard>
          </Stack>
        </>
      )}
    </Container>
  );
}

const Container = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  padding: '40px',
});

const StyledGrid = styled(Grid)({
  paddingBottom: '16px',
  paddingTop: '16px',
});

const StyledTypography = styled(Typography)({
  display: 'inline-block',
  marginRight: '3px',
  '> p': {
    margin: '0',
  },
});

const ButtonContainer = styled('div')({
  marginTop: '40px',
  alignSelf: 'flex-end',
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  gap: '7px',
  '& > *': {
    margin: '5px',
  },
});

export default PostEdit;
